import { resolutionToSeconds } from "../../constants";
import {
  getSecondsIntervalMs,
  onSecondsHistoryExtended,
} from "../../datafeed/secondsHistory";
import { INITIAL_BAR_COUNT } from "../constants";
import { priceFormatFromSymbol } from "../axes/priceFormat";
import BarStore from "./BarStore";
import { openDatafeedSession } from "./datafeedAdapter";
import { createHistoryPager, periodBefore } from "./historyPager";

const MS_PER_SECOND = 1000;
const DEFAULT_PRICE_FORMAT = { decimals: 2, minMove: 0.01 };

/** What changed in the bars, passed to `onChange` (besides REALTIME_CHANGE values). */
export const HISTORY_CHANGE = {
  // All bars were replaced: new symbol or resolution, or a reload
  REPLACED: "replaced",
  // Older bars were added on the left
  PREPENDED: "prepended",
};

/**
 * Data of the chart: the first page of history, older pages near the left
 * edge, realtime bars, symbol and resolution switches and reloads.
 * Knows nothing about drawing: every change of `store` is reported through
 * `onChange` with one of HISTORY_CHANGE / REALTIME_CHANGE.
 */
export default class DataController {
  /**
   * @param {object} datafeed - instance from createDatafeed()
   * @param {(change: string) => void} onChange
   */
  constructor(datafeed, onChange) {
    this.datafeed = datafeed;
    this.onChange = onChange;
    this.store = new BarStore();
    this.symbol = null;
    this.resolution = null;
    // Price precision of the symbol (pricescale from resolveSymbol)
    this.priceFormat = DEFAULT_PRICE_FORMAT;
    this.barDurationMs = 0;
    this.session = null;
    // Older history of the open session; null until its first page arrives
    this.pager = null;
    this.stopSecondsListener = onSecondsHistoryExtended((symbol) =>
      this.handleSecondsHistoryExtended(symbol),
    );
  }

  /** Unsubscribes from the old stream and loads the history of the new symbol. */
  setSymbol(symbol) {
    if (symbol === this.symbol) return;

    this.symbol = symbol;
    this.open({ keepBars: false });
  }

  /** Unsubscribes from the old stream and loads the history in the new resolution. */
  setResolution(resolution) {
    if (resolution === this.resolution) return;

    this.resolution = resolution;
    this.open({ keepBars: false });
  }

  /** Loads the history again and resubscribes; the old bars stay until the new ones arrive. */
  reload() {
    this.open({ keepBars: true });
  }

  /** @param {{ first: number, last: number }} range - visible bar indexes */
  handleVisibleRange(range) {
    this.pager?.handleVisibleRange(range);
  }

  destroy() {
    this.stopSecondsListener();
    this.closeSession();
  }

  open({ keepBars }) {
    this.closeSession();

    if (!keepBars) {
      this.store.setBars([]);
      this.onChange(HISTORY_CHANGE.REPLACED);
    }

    if (!this.symbol || !this.resolution) return;

    this.barDurationMs =
      resolutionToSeconds(this.resolution, getSecondsIntervalMs) * MS_PER_SECOND;
    this.session = openDatafeedSession(this.datafeed, {
      symbol: this.symbol,
      resolution: this.resolution,
      onResolved: (symbolInfo) => this.loadFirstPage(symbolInfo),
    });
  }

  closeSession() {
    this.session?.close();
    this.session = null;
    this.pager = null;
  }

  // A closed session drops its answers, so `this.session` here is always the open one
  loadFirstPage(symbolInfo) {
    const { session, store } = this;
    const to = Math.floor(Date.now() / MS_PER_SECOND);
    const period = periodBefore(to, INITIAL_BAR_COUNT, this.barDurationMs, true);

    session.getBars(period, (bars, noData) => {
      this.priceFormat = priceFormatFromSymbol(symbolInfo);
      store.setBars(bars);
      this.pager = createHistoryPager({
        session,
        store,
        barDurationMs: this.barDurationMs,
        firstPageFrom: period.from,
        hasMore: !noData,
        onPrepended: () => this.onChange(HISTORY_CHANGE.PREPENDED),
      });
      this.onChange(HISTORY_CHANGE.REPLACED);
      session.subscribe(
        (bar) => this.handleRealtimeBar(bar),
        () => this.reload(),
      );
    });
  }

  handleRealtimeBar(bar) {
    const change = this.store.applyRealtimeBar(bar);

    if (change) this.onChange(change);
  }

  // 15S / 30S history grows in the background after the first quick answer:
  // re-read it, the bars come from the cache without requests
  handleSecondsHistoryExtended(symbol) {
    const isSecondsChart = Boolean(getSecondsIntervalMs(this.resolution));

    if (isSecondsChart && String(this.symbol).toUpperCase() === symbol) this.reload();
  }
}
